import React, { useState } from 'react';
import {
  AlertTriangle, FileText, ChevronDown, ChevronRight,
  Briefcase, Search, FileSearch, BookMarked, Target, ArrowRight,
  CalendarClock, CalendarX, CalendarOff, CalendarCheck, CalendarDays,
  Inbox, Users, Gavel, ShieldCheck, Download,
} from 'lucide-react';

/* -- P72: Deadline Protection banner (Local 15 Grievance Engine spec §15) --
   Renders ABOVE everything else on every Ask Stewie answer. Shows:
     - severity-colored banner (green/yellow/orange/red/unknown)
     - incident / awareness / filing-deadline dates
     - days remaining
     - recommended action
   When dates can't be extracted, switches to an "intake" mode that prompts
   the user for the two missing dates instead of showing fake deadlines. */
const DEADLINE_TONES = {
  green:   { label: 'Filing window healthy',  icon: CalendarCheck, color: '#22c55e', bg: 'rgba(34,197,94,0.07)',   border: 'rgba(34,197,94,0.45)'  },
  yellow:  { label: 'Filing window narrowing', icon: CalendarClock, color: '#eab308', bg: 'rgba(234,179,8,0.07)',  border: 'rgba(234,179,8,0.45)'  },
  orange:  { label: 'URGENT — filing window closing',  icon: CalendarOff,   color: '#fb923c', bg: 'rgba(251,146,60,0.08)', border: 'rgba(251,146,60,0.55)' },
  red:     { label: 'WARNING — deadline may be expired', icon: CalendarX, color: '#ef4444', bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.6)'   },
  unknown: { label: 'Dates needed to track filing deadline', icon: CalendarDays, color: '#a1a1aa', bg: 'rgba(255,255,255,0.025)', border: 'rgba(255,255,255,0.12)' },
};

function _fmtDate(iso) {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return `${m}/${d}/${y}`;
  } catch { return iso; }
}

function DeadlineProtectionBanner({ deadline, onAskFollowUp }) {
  if (!deadline || !deadline.show) return null;
  const tone = DEADLINE_TONES[deadline.severity] || DEADLINE_TONES.unknown;
  const Icon = tone.icon;
  const isUnknown = deadline.severity === 'unknown';
  const missingQs = deadline.missing_date_questions || [];
  return (
    <div
      className={`sa-deadline sa-deadline-${deadline.severity}`}
      data-testid="deadline-banner"
      data-severity={deadline.severity}
      style={{
        background: tone.bg,
        borderColor: tone.border,
      }}
    >
      <div className="sa-deadline-head">
        <Icon size={15} style={{ color: tone.color }} />
        <span className="sa-deadline-label" style={{ color: tone.color }}>
          {tone.label}
        </span>
        {!isUnknown && deadline.days_remaining !== null && (
          <span
            className="sa-deadline-days"
            style={{ background: tone.color + '22', color: tone.color, borderColor: tone.color + '55' }}
            data-testid="deadline-days-remaining"
          >
            {deadline.days_remaining < 0
              ? `${Math.abs(deadline.days_remaining)} day${Math.abs(deadline.days_remaining) === 1 ? '' : 's'} PAST`
              : `${deadline.days_remaining} day${deadline.days_remaining === 1 ? '' : 's'} left`}
          </span>
        )}
      </div>

      {!isUnknown && (
        <div className="sa-deadline-grid" data-testid="deadline-dates-grid">
          <div className="sa-deadline-cell">
            <div className="sa-deadline-cell-label">Incident</div>
            <div className="sa-deadline-cell-value" data-testid="deadline-incident-date">
              {_fmtDate(deadline.incident_date)}
            </div>
          </div>
          <div className="sa-deadline-cell">
            <div className="sa-deadline-cell-label">Awareness</div>
            <div className="sa-deadline-cell-value" data-testid="deadline-awareness-date">
              {_fmtDate(deadline.awareness_date)}
            </div>
          </div>
          <div className="sa-deadline-cell">
            <div className="sa-deadline-cell-label">Filing Deadline</div>
            <div className="sa-deadline-cell-value" data-testid="deadline-filing-date">
              {_fmtDate(deadline.filing_deadline)}
            </div>
          </div>
          <div className="sa-deadline-cell">
            <div className="sa-deadline-cell-label">Window</div>
            <div className="sa-deadline-cell-value">{deadline.filing_window_days} days</div>
          </div>
        </div>
      )}

      <div className="sa-deadline-action" data-testid="deadline-recommended-action">
        {deadline.recommended_action}
      </div>

      {isUnknown && missingQs.length > 0 && (
        <div className="sa-deadline-intake" data-testid="deadline-intake-questions">
          {missingQs.map((q, i) => (
            <button
              key={`dl-q-${i}`}
              type="button"
              className="sa-deadline-intake-pill"
              data-testid={`deadline-intake-question-${i}`}
              onClick={() => onAskFollowUp && onAskFollowUp(q, i, `intake_date_${i}`)}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -- P73: Information Requests panel (Local 15 Grievance Engine spec §11) --
   Renders BELOW the six-section view, ABOVE the existing detailed rule /
   timeline / grievance-language sections. Each entry is a value-ranked
   card: HIGH (amber) / MEDIUM (blue) / LOW (grey). */
const IR_VALUE_TONES = {
  HIGH:   { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.50)', color: '#fbbf24' },
  MEDIUM: { bg: 'rgba(96,165,250,0.08)', border: 'rgba(96,165,250,0.40)', color: '#93c5fd' },
  LOW:    { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.12)', color: '#a1a1aa' },
};

function InformationRequestsPanel({ requests }) {
  if (!Array.isArray(requests) || requests.length === 0) return null;
  return (
    <div className="sa-info-requests" data-testid="information-requests-section">
      <div className="sa-info-head">
        <Inbox size={14} />
        <span>Information Requests to Consider</span>
        <span className="sa-info-count" data-testid="information-requests-count">
          {requests.length}
        </span>
      </div>
      <div className="sa-info-list">
        {requests.map((ir, i) => {
          const tone = IR_VALUE_TONES[ir.value] || IR_VALUE_TONES.MEDIUM;
          return (
            <div
              key={`${ir.request.slice(0, 30)}-${i}`}
              className="sa-info-row"
              data-testid={`information-request-${i}`}
              data-value={ir.value}
              data-issue={ir.issue_type}
              style={{ background: tone.bg, borderColor: tone.border }}
            >
              <div className="sa-info-row-head">
                <span
                  className="sa-info-value"
                  style={{ background: tone.color + '22', color: tone.color, borderColor: tone.color + '66' }}
                  data-testid={`information-request-${i}-value`}
                >
                  {ir.value}
                </span>
                <div
                  className="sa-info-request"
                  data-testid={`information-request-${i}-request`}
                >
                  {ir.request}
                </div>
              </div>
              <div className="sa-info-meta">
                <div className="sa-info-meta-row">
                  <span className="sa-info-meta-label">WHY IT IS RELEVANT:</span>
                  <span data-testid={`information-request-${i}-why`}>{ir.why}</span>
                </div>
                <div className="sa-info-meta-row">
                  <span className="sa-info-meta-label">WHAT IT MAY PROVE:</span>
                  <span data-testid={`information-request-${i}-may-prove`}>{ir.may_prove}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -- P74: Step 1 Meeting Prep panel (Local 15 Grievance Engine spec §12) --
   Renders BELOW the Information Requests panel. Collapsible by default so
   it doesn't dominate the answer for stewards who don't need it yet. Three
   sub-blocks:
     - UNION POSITION (facts / contract / past practice / evidence / remedy)
     - COMPANY POSITION (predicted management arguments)
     - UNION REBUTTALS (evidence-based / contract-based / past-practice)
*/
function Step1List({ items, testId }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="sa-s1-list" data-testid={testId}>
      {items.map((line, i) => (
        <li key={i} data-testid={`${testId}-item-${i}`}>{line}</li>
      ))}
    </ul>
  );
}

function Step1MeetingPrepPanel({ prep }) {
  const [open, setOpen] = useState(false);
  if (!prep || typeof prep !== 'object') return null;
  const up  = prep.union_position    || {};
  const cp  = prep.company_position  || {};
  const reb = prep.union_rebuttals   || {};
  // Hide entirely if EVERYTHING is empty (e.g., total failure fallback)
  const empty =
    !(up.facts || []).length &&
    !(cp.predicted_arguments || []).length &&
    !(reb.evidence_based || []).length &&
    !(reb.contract_based || []).length &&
    !(reb.past_practice_based || []).length;
  if (empty) return null;

  return (
    <div className="sa-s1" data-testid="step1-meeting-prep-section">
      <button
        type="button"
        className="sa-s1-toggle"
        onClick={() => setOpen(o => !o)}
        data-testid="step1-meeting-prep-toggle"
        aria-expanded={open}
      >
        <Gavel size={14} />
        <span>Step 1 Meeting Prep</span>
        <span className="sa-s1-chip" data-testid="step1-meeting-prep-issue">
          {prep.primary_issue}{prep.is_generic_fallback ? ' · generic' : ''}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {open && (
        <div className="sa-s1-body">

          {/* UNION POSITION */}
          <div className="sa-s1-block sa-s1-block-union" data-testid="step1-union-position">
            <div className="sa-s1-block-head">
              <ShieldCheck size={13} />
              <span>UNION POSITION</span>
            </div>
            <div className="sa-s1-grid">
              <div className="sa-s1-cell">
                <div className="sa-s1-label">FACTS</div>
                <Step1List items={up.facts} testId="step1-union-facts" />
              </div>
              <div className="sa-s1-cell">
                <div className="sa-s1-label">CONTRACT LANGUAGE</div>
                <Step1List items={up.contract_language} testId="step1-union-contract" />
              </div>
              <div className="sa-s1-cell">
                <div className="sa-s1-label">PAST PRACTICE</div>
                <Step1List items={up.past_practice} testId="step1-union-past-practice" />
              </div>
              <div className="sa-s1-cell">
                <div className="sa-s1-label">SUPPORTING EVIDENCE</div>
                <Step1List items={up.supporting_evidence} testId="step1-union-evidence" />
              </div>
              <div className="sa-s1-cell sa-s1-cell-full">
                <div className="sa-s1-label">REQUESTED REMEDY</div>
                <Step1List items={up.requested_remedy} testId="step1-union-remedy" />
              </div>
            </div>
          </div>

          {/* COMPANY POSITION */}
          <div className="sa-s1-block sa-s1-block-company" data-testid="step1-company-position">
            <div className="sa-s1-block-head">
              <Users size={13} />
              <span>PREDICTED COMPANY POSITION</span>
            </div>
            <Step1List items={cp.predicted_arguments} testId="step1-company-arguments" />
          </div>

          {/* UNION REBUTTALS */}
          <div className="sa-s1-block sa-s1-block-rebuttals" data-testid="step1-union-rebuttals">
            <div className="sa-s1-block-head">
              <Target size={13} />
              <span>UNION REBUTTALS</span>
            </div>
            <div className="sa-s1-grid sa-s1-grid-3">
              <div className="sa-s1-cell">
                <div className="sa-s1-label">EVIDENCE-BASED</div>
                <Step1List items={reb.evidence_based} testId="step1-rebuttals-evidence" />
              </div>
              <div className="sa-s1-cell">
                <div className="sa-s1-label">CONTRACT-BASED</div>
                <Step1List items={reb.contract_based} testId="step1-rebuttals-contract" />
              </div>
              <div className="sa-s1-cell">
                <div className="sa-s1-label">PAST-PRACTICE-BASED</div>
                <Step1List items={reb.past_practice_based} testId="step1-rebuttals-past-practice" />
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

/* -- P75: Joint Position Summary panel (Local 15 Grievance Engine spec §13) --
   Renders BELOW the Step 1 Meeting Prep panel. Collapsible. Includes a
   one-click "Export PDF" button that posts the (possibly edited) JPS
   payload back to the server for rendering. */
function JpsTextarea({ value, onChange, rows = 2, testId, placeholder }) {
  return (
    <textarea
      className="sa-jps-textarea"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testId}
      placeholder={placeholder}
    />
  );
}

function JpsList({ items, testId }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="sa-jps-empty">—</span>;
  }
  return (
    <ul className="sa-jps-list" data-testid={testId}>
      {items.map((line, i) => (
        <li key={i} data-testid={`${testId}-item-${i}`}>{line}</li>
      ))}
    </ul>
  );
}

function JointPositionSummaryPanel({ jps, sourceQuestion }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [resolvedDraft, setResolvedDraft] = useState('');
  const [unresolvedDraft, setUnresolvedDraft] = useState('');

  if (!jps || typeof jps !== 'object') return null;
  const up = jps.union_position || {};
  const reb = (up.rebuttals || {});
  const empty =
    !(jps.facts || []).length &&
    !(jps.company_position || []).length &&
    !(up.facts || []).length &&
    !jps.nature_of_grievance;
  if (empty) return null;

  const splitLines = (s) =>
    (s || '')
      .split(/\n+/)
      .map(x => x.trim())
      .filter(Boolean);

  async function exportPdf() {
    try {
      setExporting(true);
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      // Merge in the steward's typed resolved/unresolved edits.
      const exportPayload = {
        jps: {
          ...jps,
          resolved_issues: [
            ...(jps.resolved_issues || []),
            ...splitLines(resolvedDraft),
          ],
          unresolved_issues: splitLines(unresolvedDraft).length
            ? splitLines(unresolvedDraft)
            : (jps.unresolved_issues || []),
        },
        case_meta: {
          title: sourceQuestion?.slice(0, 80) || 'Grievance',
          step: 'Step 1',
        },
      };
      const res = await fetch(`${apiBase}/api/ask-stewie/joint-position-summary/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exportPayload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jps_${(jps.primary_issue || 'grievance')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('JPS PDF export failed:', err);
    }
    setExporting(false);
  }

  return (
    <div className="sa-jps" data-testid="jps-section">
      <button
        type="button"
        className="sa-jps-toggle"
        onClick={() => setOpen(o => !o)}
        data-testid="jps-toggle"
        aria-expanded={open}
      >
        <FileText size={14} />
        <span>Joint Position Summary</span>
        <span className="sa-jps-chip" data-testid="jps-issue">
          {jps.primary_issue}{jps.is_generic_fallback ? ' · generic' : ''}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {open && (
        <div className="sa-jps-body">
          {/* 1. NATURE OF GRIEVANCE */}
          <div className="sa-jps-block" data-testid="jps-nature">
            <div className="sa-jps-label">1.  Nature of Grievance</div>
            <p className="sa-jps-text" data-testid="jps-nature-text">{jps.nature_of_grievance}</p>
          </div>

          {/* 2. FACTS */}
          <div className="sa-jps-block" data-testid="jps-facts-block">
            <div className="sa-jps-label">2.  Facts</div>
            <JpsList items={jps.facts} testId="jps-facts" />
          </div>

          {/* 3. COMPANY POSITION */}
          <div className="sa-jps-block sa-jps-block-company" data-testid="jps-company-block">
            <div className="sa-jps-label">3.  Company Position</div>
            <JpsList items={jps.company_position} testId="jps-company" />
          </div>

          {/* 4. UNION POSITION */}
          <div className="sa-jps-block sa-jps-block-union" data-testid="jps-union-block">
            <div className="sa-jps-label">4.  Union Position</div>
            <div className="sa-jps-up-grid">
              <div>
                <div className="sa-jps-sublabel">Facts</div>
                <JpsList items={up.facts} testId="jps-union-facts" />
              </div>
              <div>
                <div className="sa-jps-sublabel">Contract Language</div>
                <JpsList items={up.contract_language} testId="jps-union-contract" />
              </div>
              <div>
                <div className="sa-jps-sublabel">Past Practice</div>
                <JpsList items={up.past_practice} testId="jps-union-past-practice" />
              </div>
              <div>
                <div className="sa-jps-sublabel">Supporting Evidence</div>
                <JpsList items={up.supporting_evidence} testId="jps-union-evidence" />
              </div>
              <div className="sa-jps-up-full">
                <div className="sa-jps-sublabel">Requested Remedy</div>
                <JpsList items={up.requested_remedy} testId="jps-union-remedy" />
              </div>
              {(reb.evidence_based?.length || reb.contract_based?.length || reb.past_practice_based?.length) ? (
                <div className="sa-jps-up-full">
                  <div className="sa-jps-sublabel">Union Rebuttals</div>
                  <div className="sa-jps-reb-grid">
                    <div>
                      <div className="sa-jps-mini-label">Evidence-based</div>
                      <JpsList items={reb.evidence_based} testId="jps-rebuttals-evidence" />
                    </div>
                    <div>
                      <div className="sa-jps-mini-label">Contract-based</div>
                      <JpsList items={reb.contract_based} testId="jps-rebuttals-contract" />
                    </div>
                    <div>
                      <div className="sa-jps-mini-label">Past-practice-based</div>
                      <JpsList items={reb.past_practice_based} testId="jps-rebuttals-past-practice" />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* 5. RESOLVED ISSUES (editable) */}
          <div className="sa-jps-block sa-jps-block-resolved" data-testid="jps-resolved-block">
            <div className="sa-jps-label">5.  Resolved Issues</div>
            <p className="sa-jps-hint">Record any agreement reached at the meeting (one item per line).</p>
            <JpsTextarea
              value={resolvedDraft}
              onChange={setResolvedDraft}
              rows={2}
              testId="jps-resolved-input"
              placeholder="e.g. Company agreed to remove the written warning from the file."
            />
          </div>

          {/* 6. UNRESOLVED ISSUES (pre-populated, editable) */}
          <div className="sa-jps-block sa-jps-block-unresolved" data-testid="jps-unresolved-block">
            <div className="sa-jps-label">6.  Unresolved Issues</div>
            <p className="sa-jps-hint">These escalate to Step 2 (edit one item per line to override the defaults).</p>
            <JpsList items={jps.unresolved_issues} testId="jps-unresolved" />
            <JpsTextarea
              value={unresolvedDraft}
              onChange={setUnresolvedDraft}
              rows={2}
              testId="jps-unresolved-input"
              placeholder="Override / add your own unresolved items here"
            />
          </div>

          <div className="sa-jps-actions">
            <button
              type="button"
              className="sa-jps-export-btn"
              onClick={exportPdf}
              disabled={exporting}
              data-testid="jps-export-pdf-btn"
            >
              <Download size={14} />
              {exporting ? 'Generating PDF…' : 'Export JPS as PDF'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -- Six-Section View (Local 15 Grievance Engine spec §16) --
   Renders FACTS / POTENTIAL VIOLATION / EVIDENCE NEEDED /
   CONTRACT LANGUAGE / RECOMMENDED REMEDY / NEXT STEPS as the headline
   structure on every answer. Strictly additive — original detail
   sections (rule card, decision log, grievance language, follow-up
   pills, etc.) still render below this view. */
const SIX_SECTIONS = [
  { key: 'facts',                label: 'Facts',                        icon: Search,        tone: 'neutral' },
  { key: 'potential_violation',  label: 'Potential Violation',          icon: AlertTriangle, tone: 'warn' },
  { key: 'evidence_needed',      label: 'Evidence Needed',              icon: FileSearch,    tone: 'info' },
  { key: 'contract_language',    label: 'Contract Language to Review',  icon: BookMarked,    tone: 'neutral' },
  { key: 'recommended_remedy',   label: 'Recommended Remedy',           icon: Target,        tone: 'success' },
  { key: 'next_steps',           label: 'Next Steps',                   icon: ArrowRight,    tone: 'info' },
];

const _FALLBACK = 'Insufficient information — please answer the follow-up questions';

function SixSectionItem({ section, value }) {
  const Icon = section.icon;
  const isList = Array.isArray(value);
  const isFallback = (
    (typeof value === 'string' && value.startsWith(_FALLBACK)) ||
    (isList && value.length === 1 && value[0].startsWith(_FALLBACK)) ||
    (isList && value.length === 0)
  );
  return (
    <div
      className={`sa-six-row sa-six-${section.tone}${isFallback ? ' sa-six-empty' : ''}`}
      data-testid={`six-section-${section.key}`}
    >
      <div className="sa-six-head">
        <Icon size={13} />
        <span>{section.label.toUpperCase()}</span>
      </div>
      <div className="sa-six-body">
        {isList ? (
          <ul className="sa-six-list">
            {value.map((item, i) => (
              <li
                key={`${section.key}-${i}`}
                data-testid={`six-section-${section.key}-item-${i}`}
              >
                {item}
              </li>
            ))}
          </ul>
        ) : (
          <div className="sa-six-text">{value || _FALLBACK}</div>
        )}
      </div>
    </div>
  );
}

function SixSectionView({ sections }) {
  if (!sections || typeof sections !== 'object') return null;
  return (
    <div className="sa-six" data-testid="response-sections">
      <div className="sa-six-strip-head">
        <Briefcase size={13} />
        <span>Grievance Brief — Local 15</span>
      </div>
      {SIX_SECTIONS.map((s) => (
        <SixSectionItem key={s.key} section={s} value={sections[s.key]} />
      ))}
    </div>
  );
}

export {
  DeadlineProtectionBanner,
  InformationRequestsPanel,
  Step1MeetingPrepPanel,
  JointPositionSummaryPanel,
  SixSectionView,
};
